import z from 'zod'

export const KIND_GROUPS = ['trips', 'top_up', 'missed', 'refund'] as const
export type TKindGroup = (typeof KIND_GROUPS)[number]

export const MODALITIES = ['train', 'bus', 'tram', 'metro'] as const
export type TModality = (typeof MODALITIES)[number]

export type TTransactionSortBy = 'startTimestamp' | 'price'

export interface ITransactionsSearch {
  page?: number
  /**
   * Column id. The user list only writes a date or an amount. The company list on the same
   * `/transactions` route sorts by its own columns, so any non-empty id is kept.
   */
  sortBy?: string
  sortDir?: 'asc' | 'desc'
  /** Super-admin card-number filter. Digits only; spaces are stripped before this is written. */
  cardNumber?: string
  /** Traveller filter: one shared card, layered on that traveller's loan windows. */
  productId?: string
  kindGroup?: TKindGroup
  modality?: TModality[]
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const kindGroupSchema = z.enum(KIND_GROUPS)
const modalitySchema = z.enum(MODALITIES)
const sortDirSchema = z.enum(['asc', 'desc'])

export interface ITransactionSort {
  sortBy: TTransactionSortBy
  sortDir: 'asc' | 'desc'
}

/** Any sort writes sortBy and sortDir and drops the page. */
export function withSort<T extends object>(
  prev: T,
  sort: ITransactionSort,
): T & { sortBy: TTransactionSortBy; sortDir: 'asc' | 'desc'; page: undefined } {
  return { ...prev, sortBy: sort.sortBy, sortDir: sort.sortDir, page: undefined }
}

/** Date sort writes sortBy and sortDir and drops the page, so the first page of the new order loads. */
export function withDateSort<T extends object>(
  prev: T,
  sortDir: 'asc' | 'desc',
): T & { sortBy: 'startTimestamp'; sortDir: 'asc' | 'desc'; page: undefined } {
  return { ...withSort(prev, { sortBy: 'startTimestamp', sortDir }), sortBy: 'startTimestamp' }
}

/** Kind and modality filters replace the previous pair and drop the page. An empty modality leaves the URL. */
export function withFilters<T extends object>(
  prev: T,
  filters: { kindGroup?: TKindGroup; modality?: TModality[] },
): T & { kindGroup: TKindGroup | undefined; modality: TModality[] | undefined; page: undefined } {
  return {
    ...prev,
    kindGroup: filters.kindGroup,
    modality: filters.modality && filters.modality.length > 0 ? filters.modality : undefined,
    page: undefined,
  }
}

function asStringList(value: unknown): string[] {
  if (typeof value === 'string') return [value]
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string')

  return []
}

/** Shared search schema for the self and admin transaction routes. */
export function validateTransactionsSearch(search: Record<string, unknown>): ITransactionsSearch {
  const page = Number(search.page)
  const sortDir = sortDirSchema.safeParse(search.sortDir)
  const kindGroup = kindGroupSchema.safeParse(search.kindGroup)
  const modality = asStringList(search.modality).flatMap((item): TModality[] => {
    const parsed = modalitySchema.safeParse(item)

    return parsed.success ? [parsed.data] : []
  })

  return {
    page: Number.isFinite(page) && page > 1 ? page : undefined,
    sortBy: typeof search.sortBy === 'string' && search.sortBy.length > 0 ? search.sortBy : undefined,
    sortDir: sortDir.success ? sortDir.data : undefined,
    cardNumber:
      typeof search.cardNumber === 'string' && /^\d+$/.test(search.cardNumber) ? search.cardNumber : undefined,
    productId:
      typeof search.productId === 'string' && UUID_PATTERN.test(search.productId) ? search.productId : undefined,
    kindGroup: kindGroup.success ? kindGroup.data : undefined,
    modality: modality.length > 0 ? modality : undefined,
  }
}
